import { MerchantState, TikTokShopConnection, TTSMerchantTier } from 'model/types/merchant';

export function deriveTierRestrictions(connection: TikTokShopConnection | undefined): TTSMerchantTier | undefined {
  if (connection?.tier == null) {
    return undefined;
  }

  const { tier, tierUnlimited, tierFollowerCap, tierQuotaTotal, tierQuotaUsed, tierQuotaRemaining } = connection;

  let bucket: TTSMerchantTier['bucket'];
  if (tier === 0) {
    bucket = 'starter';
  } else if (tierUnlimited && tier === 3) {
    bucket = 'topTier';
  } else {
    bucket = 'other';
  }

  let reportedRemaining: number | null;
  if (typeof tierQuotaRemaining === 'number') {
    reportedRemaining = tierQuotaRemaining;
  } else if (typeof tierQuotaTotal === 'number' && typeof tierQuotaUsed === 'number') {
    reportedRemaining = tierQuotaTotal - tierQuotaUsed;
  } else {
    reportedRemaining = null;
  }
  const quotaRemaining = reportedRemaining === null ? null : Math.max(0, reportedRemaining);

  const isNearLimit =
    bucket !== 'topTier' &&
    quotaRemaining !== null &&
    typeof tierQuotaTotal === 'number' &&
    tierQuotaTotal > 0 &&
    quotaRemaining / tierQuotaTotal <= 0.1;

  return {
    bucket,
    unlimited: Boolean(tierUnlimited),
    quotaTotal: tierQuotaTotal,
    quotaUsed: tierQuotaUsed,
    quotaRemaining,
    followerCap: tierFollowerCap,
    isNearLimit
  };
}

/**
 * A merchant can hold several TikTok Shop connections; tier data is only ever read off the first
 * one. Owning that unwrap here keeps `edges[0].node` out of the reducer and the fetch hook.
 */
export function tierFromMerchantResponse(merchant: MerchantState['merchant'] | undefined): TTSMerchantTier | undefined {
  return deriveTierRestrictions(merchant?.tiktokShopConnections?.edges?.[0]?.node);
}

/**
 * `null` carries a different meaning per field, so every consumer interprets it through one of the
 * predicates below rather than comparing inline.
 *
 * A `followerCap` of `null` means TikTok imposes no follower ceiling; a cap of `0` means the
 * merchant may not invite creators directly at all. `quotaRemaining` of `null` means either no
 * quota or an unreported one, which only `unlimited` disambiguates.
 */
export function hasFollowerCap(tier: TTSMerchantTier | undefined): tier is TTSMerchantTier & { followerCap: number } {
  return tier?.followerCap != null;
}

export function canInviteDirectly(tier: TTSMerchantTier | undefined): boolean {
  return tier?.followerCap !== 0;
}

export function hasUnlimitedConnections(tier: TTSMerchantTier | undefined): boolean {
  return tier?.unlimited === true;
}
